/**
 * Sprint PERMUTA-UX (PARTE A.2) — VeicoloPicker
 *
 * Selettore con ricerca per scegliere un veicolo (sostituisce window.prompt
 * per "aggancia veicolo esistente" nel popup permuta).
 *
 * Pattern coerente con AnagraficaPicker / DepositoPicker: dialog ricerca con
 * input + lista cliccabile. Rispetta lo scope del backend (GET /vehicles).
 *
 * Props:
 *  - open, onOpenChange: controllo dialog
 *  - onSelect(vehicle): callback con l'oggetto veicolo scelto
 *  - excludeId: id da escludere dai risultati (es. il veicolo principale
 *    della pratica corrente non può essere anche permuta di se stesso)
 *  - contextCompanyId: limita allo scope dell'azienda della pratica
 *  - testIdPrefix
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Search } from "lucide-react";

export default function VeicoloPicker({
  open, onOpenChange, onSelect,
  excludeId = null, contextCompanyId = null,
  testIdPrefix = "veicolo-picker",
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) { setQ(""); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams();
    if (q.trim()) params.set("search", q.trim());
    if (contextCompanyId) params.set("company_id", contextCompanyId);
    params.set("page_size", "60");
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/vehicles?${params.toString()}`)
        .then((r) => {
          const all = r.data?.items || [];
          setItems(all.filter((v) => v.id !== excludeId));
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [open, q, excludeId, contextCompanyId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Scegli veicolo</DialogTitle>
          <DialogDescription>
            Cerca per codice, targa, marca, modello o telaio.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Codice, targa, marca, modello, telaio..."
                   className="pl-8" autoFocus
                   data-testid={`${testIdPrefix}-search-input`} />
          </div>
          <div className="border border-slate-200 rounded-md max-h-[420px] overflow-y-auto divide-y">
            {loading ? (
              <div className="p-6 text-center text-slate-400 italic">Caricamento…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-slate-400 italic">Nessun veicolo trovato</div>
            ) : items.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => onSelect(v)}
                className="w-full text-left px-3 py-2 hover:bg-blue-50/50 flex items-center justify-between gap-3"
                data-testid={`${testIdPrefix}-result-${v.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-blue-700">{v.codice}</span>
                    <span className="font-medium truncate">
                      {v.marca} {v.modello} {v.versione || ""}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 flex gap-3 mt-0.5">
                    <span className="font-mono">{v.targa || "—"}</span>
                    <span>{v.km != null ? `${v.km} km` : "—"}</span>
                    <span>{v.prima_immatricolazione?.slice(0, 4) || "—"}</span>
                  </div>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                  {v.disponibilita_fisica || v.status}
                </span>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
